import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useFlowActions, useFlows } from '@/hooks/use-flows'
import {
  createDocument,
  getFlowCatalog,
  type Flow,
  type FlowCatalogStep,
} from '@/lib/api'
import { FlowStudioPulse } from './flow-studio-pulse'
import { testIds } from '@/lib/test-ids'
import { useWorkspace } from '@/providers/workspace-provider'
import {
  FLOW_STUDIO_SURFACE_LABEL,
  rememberFlowTab,
  type FlowStudioSurface,
} from './flow-studio-events'

function SurfaceHeader({ title }: { title: string }) {
  return (
    <div className="flex h-10 shrink-0 items-center border-b border-border px-4">
      <h1 className="text-sm font-semibold">{title}</h1>
    </div>
  )
}

export function FlowStudioMain({
  surface,
  onOpenDocument,
}: {
  surface: FlowStudioSurface
  onOpenDocument: (documentId: string, event?: MouseEvent) => void
}) {
  const { setPosture } = useFlowActions()
  const { activeWorkspaceIds } = useWorkspace()
  const workspaceId = activeWorkspaceIds[0]
  const [catalog, setCatalog] = useState<FlowCatalogStep[]>([])
  const [composerTitle, setComposerTitle] = useState('')
  const [composerPrompt, setComposerPrompt] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (surface !== 'gallery' || catalog.length > 0) return
    void getFlowCatalog().then(setCatalog).catch(() => setCatalog([]))
  }, [catalog.length, surface])

  const openFlow = (flowId: string, tab: 'definition' | 'runs', event?: MouseEvent) => {
    rememberFlowTab(flowId, tab)
    onOpenDocument(flowId, event)
  }

  const createShadowFlow = async (title: string, content: string) => {
    setBusy(true)
    try {
      const created = await createDocument({
        title,
        content,
        document_type: 'flow',
        folder_path: 'flows',
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
      })
      const id = created.document?.id
      if (!id) throw new Error('No document id')
      await setPosture(id, 'shadow')
      toast.success('Created in Shadow')
      openFlow(id, 'definition')
      return id
    } catch {
      toast.error('Could not create the flow')
      return null
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background" data-testid={testIds.flowStudioIndex.root}>
      <SurfaceHeader title={FLOW_STUDIO_SURFACE_LABEL[surface]} />
      <div className={surface === 'pulse' ? 'flex min-h-0 flex-1 overflow-hidden' : 'min-h-0 flex-1 overflow-y-auto'}>
        {surface === 'pulse' && (
          <FlowStudioPulse
            workspaceIds={activeWorkspaceIds}
            onOpenFlow={openFlow}
          />
        )}

        {surface === 'gallery' && catalog.map((step) => (
          <div key={step.id} className="flex items-center gap-3 border-b border-border px-4 py-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{step.id}</p>
              <p className="text-xs text-muted-foreground">{step.dangerous ? 'Side effect' : 'Read'}</p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7"
              disabled={busy}
              onClick={() => {
                const slug = step.id.replace(/\./g, '-')
                void createShadowFlow(step.id, [
                  '---',
                  `slug: ${slug}`,
                  'kind: flow',
                  'triggers:',
                  '  - id: schedule.cron',
                  '    config:',
                  '      expr: "0 8 * * 1-5"',
                  'steps:',
                  `  - id: ${step.id}`,
                  '    inputs: {}',
                  '---',
                  '',
                ].join('\n'))
              }}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add
            </Button>
          </div>
        ))}
        {surface === 'gallery' && catalog.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">No steps in the catalog.</p>
        )}

        {surface === 'sources' && <SourcesList onOpen={openFlow} />}

        {surface === 'composer' && (
          <form
            className="mx-auto flex w-full max-w-[760px] flex-col gap-3 p-4"
            onSubmit={(event) => {
              event.preventDefault()
              const title = composerTitle.trim() || 'New flow'
              const prompt = composerPrompt.trim() || 'Describe the flow.'
              void createShadowFlow(title, [
                '---',
                `slug: ${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'new-flow'}`,
                'kind: flow',
                'triggers:',
                '  - id: schedule.cron',
                '    config:',
                '      expr: "0 8 * * 1-5"',
                'steps:',
                '  - id: ai.session',
                '    inputs:',
                `      prompt: ${JSON.stringify(prompt)}`,
                '---',
                '',
              ].join('\n')).then((id) => {
                if (!id) return
                window.dispatchEvent(new CustomEvent('flownotes:open-chat-prompt', {
                  detail: {
                    query: `Refine this flow: ${prompt}`,
                    newThread: true,
                    documentReference: { id, title, type: 'flow', path: id },
                  },
                }))
              })
            }}
          >
            <Input value={composerTitle} onChange={(event) => setComposerTitle(event.target.value)} placeholder="Flow name" />
            <Input value={composerPrompt} onChange={(event) => setComposerPrompt(event.target.value)} placeholder="What should it do?" />
            <Button type="submit" size="sm" className="w-fit" disabled={busy}>Create in Shadow</Button>
          </form>
        )}
      </div>
    </div>
  )
}

function SourcesList({
  onOpen,
}: {
  onOpen: (flowId: string, tab: 'definition' | 'runs', event?: MouseEvent) => void
}) {
  const { flows } = useFlows()
  const groups = useMemo(() => {
    const map = new Map<string, Flow[]>()
    for (const flow of flows) {
      const trigger = flow.triggers[0]?.id || 'none'
      const list = map.get(trigger) ?? []
      list.push(flow)
      map.set(trigger, list)
    }
    return Array.from(map.entries())
  }, [flows])

  if (groups.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">No flows yet.</p>
  }

  return (
    <div>
      {groups.map(([trigger, list]) => (
        <div key={trigger}>
          <p className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{trigger}</p>
          {list.map((flow) => (
            <button
              key={flow.id}
              type="button"
              className="flex w-full border-b border-border px-4 py-2 text-left text-sm hover:bg-muted/40"
              onClick={(event) => onOpen(flow.id, 'definition', event)}
            >
              {flow.title}
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}
